"use client";

import { clientConfig } from "@/lib/config/client-config";
import { useCurrencyStore } from "@/store/currencyStore";
import { useQuery } from "@tanstack/react-query";

interface UsdtRatesData {
  rates: Record<string, number>;
}

interface UsdtRatesResponse {
  data: UsdtRatesData | null;
}

const normalizeRatesResponse = (payload: unknown): UsdtRatesData | null => {
  if (!payload || typeof payload !== "object") return null;

  if ("data" in payload) {
    const dataPayload = payload as UsdtRatesResponse;
    return dataPayload.data ?? null;
  }

  return payload as UsdtRatesData;
};

/**
 * Fetch USDT conversion rates from the API and update the currency store
 */
export const useCurrencyRates = () => {
  const { setRates } = useCurrencyStore();

  const query = useQuery<UsdtRatesData | null>({
    queryKey: ["usdt-rates"],
    queryFn: async () => {
      const response = await fetch(`${clientConfig.apiUrl}/v2/price/usdt-rates`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "X-Token": clientConfig.apiToken,
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch USDT rates: ${response.status}`);
      }

      const payload = await response.json();
      const data = normalizeRatesResponse(payload);

      if (data) {
        setRates(data.rates);
      }

      return data;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes - rates don't change frequently
    gcTime: 10 * 60 * 1000, // 10 minutes
    refetchInterval: 5 * 60 * 1000, // Refetch every 5 minutes
    retry: 2,
  });

  return {
    rates: query.data?.rates ?? null,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  };
};
